import { useState, useEffect, useRef, useCallback } from 'react';
import { Video as VideoIcon, ExternalLink } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@easyflow/ui-frontend/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import { apiClient } from '@/lib/api';
import { useSocketOptional } from '@/contexts/SocketContext';
import { cn } from '@/lib/utils';

interface ActiveRoom {
  id: string;
  title: string;
  started_at: string;
  participant_count: number;
  created_by_name?: string;
}

export default function ActiveVideoCallIndicator() {
  const [activeRooms, setActiveRooms] = useState<ActiveRoom[]>([]);
  const [open, setOpen] = useState(false);
  const [durations, setDurations] = useState<Map<string, string>>(new Map());
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const socketCtx = useSocketOptional();
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchActiveRooms = useCallback(async () => {
    try {
      const res = await apiClient.get<{ rooms?: Array<{ id: string; title?: string; started_at?: string; active_participants?: string; creator_name?: string }> }>('/video/rooms?status=in-progress&limit=20');
      if (res.success && res.data?.rooms) {
        setActiveRooms(res.data.rooms.map((r) => ({
          id: r.id,
          title: r.title || 'Video Call',
          started_at: r.started_at || '',
          participant_count: parseInt(r.active_participants || '') || 0,
          created_by_name: r.creator_name,
        })));
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchActiveRooms();
    const interval = setInterval(fetchActiveRooms, 60000);
    return () => clearInterval(interval);
  }, [fetchActiveRooms]);

  useEffect(() => {
    if (!socketCtx) return;

    const handleRoomStarted = () => {
      fetchActiveRooms();
    };

    const handleRoomEnded = (data: { roomId: string }) => {
      setActiveRooms(prev => prev.filter(r => r.id !== data.roomId));
    };

    socketCtx.on('video:room-started', handleRoomStarted);
    socketCtx.on('video:room-ended', handleRoomEnded);

    return () => {
      socketCtx.off('video:room-started', handleRoomStarted);
      socketCtx.off('video:room-ended', handleRoomEnded);
    };
  }, [socketCtx, fetchActiveRooms]);

  const formatDuration = useCallback((startedAt: string): string => {
    const start = new Date(startedAt).getTime();
    const now = Date.now();
    const diffSec = Math.max(0, Math.floor((now - start) / 1000));
    const h = Math.floor(diffSec / 3600);
    const m = Math.floor((diffSec % 3600) / 60);
    const s = diffSec % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    if (activeRooms.length === 0) {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
      return;
    }

    const updateDurations = () => {
      const newDurations = new Map<string, string>();
      activeRooms.forEach(room => {
        newDurations.set(room.id, formatDuration(room.started_at));
      });
      setDurations(newDurations);
    };

    updateDurations();
    durationTimerRef.current = setInterval(updateDurations, 1000);

    return () => {
      if (durationTimerRef.current) {
        clearInterval(durationTimerRef.current);
        durationTimerRef.current = null;
      }
    };
  }, [activeRooms, formatDuration]);

  if (activeRooms.length === 0) return null;

  const firstRoom = activeRooms[0];
  const shortestDuration = firstRoom
    ? durations.get(firstRoom.id) || '0:00'
    : '0:00';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="relative hover:bg-white/50 hover:scale-105 transition-all duration-200 p-2 gap-1.5"
              >
                <div className="relative">
                  <VideoIcon className="h-5 w-5 text-red-500" />
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5 border-2 border-white dark:border-gray-900 animate-pulse">
                    {activeRooms.length}
                  </span>
                </div>
                <span className="hidden sm:inline text-xs font-mono text-red-600 dark:text-red-400 tabular-nums">
                  {shortestDuration}
                </span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{activeRooms.length} video call attiv{activeRooms.length === 1 ? 'a' : 'e'}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <VideoIcon className="h-4 w-4 text-red-500" />
            Video Call Attive ({activeRooms.length})
          </h4>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {activeRooms.map(room => (
            <div
              key={room.id}
              className={cn(
                "px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer",
                "border-b last:border-b-0 transition-colors"
              )}
              onClick={() => {
                navigate(`/${tenantSlug}/video/${room.id}`);
                setOpen(false);
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{room.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {room.participant_count} partecipant{room.participant_count === 1 ? 'e' : 'i'}
                    </span>
                    {room.created_by_name && (
                      <>
                        <span className="text-xs text-gray-300 dark:text-gray-600">&middot;</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {room.created_by_name}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span className="text-xs font-mono text-red-600 dark:text-red-400 tabular-nums flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                    {durations.get(room.id) || '0:00'}
                  </span>
                  <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
